// src/App.tsx

import React, { useEffect, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { MsalProvider, useMsal } from "@azure/msal-react";
import { EventType, IPublicClientApplication } from "@azure/msal-browser";
import { Toaster } from 'react-hot-toast';
import RouteTracker from './RouteTracker';
import CookieBanner from './components/CookieBanner';
import { AuthProvider } from './hooks/useAuth';
import { applyAnalyticsConsent, restoreAnalyticsConsentFromCookie } from './utils/consent';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from './utils/appInsights';
import ErrorBoundary from './components/ErrorBoundary';

import Layout from './layouts/Layout';
import { persistAppLang } from './i18n';
import i18n from './i18n';
import { parseCrawlLocale } from './utils/localePath';
import LandingPage from './pages/LandingPage';
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));
const StateRequiredRoute = lazy(() => import('./components/StateRequiredRoute'));
const ComparisonStateRequiredRoute = lazy(() => import('./components/ComparisonStateRequiredRoute'));

const ContactPage = lazy(() => import('./pages/ContactPage'));
const SelectUseCasePage = lazy(() => import('./pages/SelectUseCasePage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const ArchitectureAssessment = lazy(() => import('./pages/ArchitectureAssessment'));
const WhyUsPage = lazy(() => import('./pages/WhyUsPage'));
const RoadmapPage = lazy(() => import('./pages/RoadmapPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const SolutionsHubPage = lazy(() => import('./pages/SolutionsHubPage'));
const SolutionPage = lazy(() => import('./pages/SolutionPage'));
const FaqPage = lazy(() => import('./pages/FaqPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const FeatureSoonPage = lazy(() => import('./pages/FeatureSoonPage'));
const ComparisonPage = lazy(() => import('./pages/ComparisonPage'));
const AgenticAssessment = lazy(() => import('./pages/AgenticAssessment'));
const AgenticRouteGate = lazy(() => import('./components/agentic/AgenticRouteGate'));
const DashboardLayout = lazy(() => import('./layouts/DashboardLayout'));
const ProjectListPage = lazy(() => import('./pages/ProjectListPage'));
const ProjectViewPage = lazy(() => import('./pages/ProjectViewPage'));
const LearningReviewPage = lazy(() => import('./pages/LearningReviewPage'));
const KnowledgeBuilderPage = lazy(() => import('./pages/KnowledgeBuilderPage'));
const OrgPreferencesPage = lazy(() => import('./pages/OrgPreferencesPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const PatternCatalogPage = lazy(() => import('./pages/PatternCatalogPage'));
const TechnologyCatalogPage = lazy(() => import('./pages/TechnologyCatalogPage'));
const ArchitectureDeskPage = lazy(() => import('./pages/ArchitectureDeskPage'));
const PortfolioDashboardPage = lazy(() => import('./pages/PortfolioDashboardPage'));
const WorkshopContributePage = lazy(() => import('./pages/WorkshopContributePage'));

const AppContent: React.FC = () => {
  const { instance } = useMsal();
  const location = useLocation();
  const navigate = useNavigate();
  const { lang: crawlLang, path: routePath } = parseCrawlLocale(location.pathname);

  useEffect(() => {
    if (crawlLang !== 'pl') return;
    if (i18n.language?.startsWith('pl')) return;
    persistAppLang('pl');
    void i18n.changeLanguage('pl');
  }, [crawlLang]);

  useEffect(() => {
    void restoreAnalyticsConsentFromCookie();
  }, []);

  useEffect(() => {
    const callbackId = instance.addEventCallback((event: any) => {
      const isLoginSuccess = event.eventType === EventType.LOGIN_SUCCESS;
      const isAcquireTokenSuccess = event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS;

      if ((isLoginSuccess || isAcquireTokenSuccess) && event.payload.account) {
        instance.setActiveAccount(event.payload.account);
      }
    });

    // Cold reload: restore active account only after MSAL initialize (see index msalInitPromise).
    try {
      const existing = instance.getAllAccounts();
      if (!instance.getActiveAccount() && existing[0]) {
        instance.setActiveAccount(existing[0]);
      }
    } catch (err) {
      console.warn('[auth] restore active account skipped:', err);
    }

    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [instance, navigate]);

  const handleAcceptCookies = () => {
    void applyAnalyticsConsent(true);
  };

  const handleDeclineCookies = () => {
    void applyAnalyticsConsent(false);
  };

  // Do not block the whole app on MSAL inProgress — that delayed LCP on / to tens of seconds.
  // ProtectedRoute still waits for auth; public pages paint as guest until session resolves.
  // Lazy route Suspense lives on Layout `<Outlet>` so Navbar/footer stay visible (RouteLoadingShell).

  return (
    <>
      <Toaster
        position="top-center"
        reverseOrder={false}
        containerStyle={{ zIndex: 999999 }}
        toastOptions={{
          className: 'bg-gray-800 text-white border border-gray-700',
          style: { borderRadius: '10px', background: '#1f2937', color: '#fff' },
        }}
      />
      <RouteTracker />

      <Routes location={{ ...location, pathname: routePath }}>
        <Route path="/" element={<Layout />}>
          <Route index element={<LandingPage />} />
          <Route path="how-it-works" element={<HowItWorksPage />} />
          <Route path="why-us" element={<WhyUsPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="roadmap" element={<RoadmapPage />} />
          <Route path="blog" element={<BlogPage />} />
          <Route path="blog/:slug" element={<BlogPostPage />} />
          <Route path="solutions" element={<SolutionsHubPage />} />
          <Route path="solutions/:slug" element={<SolutionPage />} />
          <Route path="faq" element={<FaqPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="privacy" element={<PrivacyPolicyPage />} />
          <Route path="cookies" element={<CookiePolicyPage />} />
          <Route path="feature-soon" element={<FeatureSoonPage />} />
          <Route path="start" element={<SelectUseCasePage />} />
          <Route path="agentic" element={<AgenticRouteGate><AgenticAssessment /></AgenticRouteGate>} />
          <Route path="workshop/:token" element={<WorkshopContributePage />} />
          <Route path="compare" element={<ComparisonStateRequiredRoute><ComparisonPage /></ComparisonStateRequiredRoute>} />
          <Route path="assessment" element={<StateRequiredRoute><ArchitectureAssessment /></StateRequiredRoute>} />

          <Route path="dashboard" element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
          >
            <Route index element={<ProjectListPage />} />
            <Route path="projects" element={<ProjectListPage />} />
            <Route path="portfolio" element={<PortfolioDashboardPage />} />
            <Route path="projects/:id" element={<ProjectViewPage />} />
            <Route path="learning" element={<LearningReviewPage />} />
            <Route path="knowledge" element={<KnowledgeBuilderPage />} />
            <Route path="preferences" element={<OrgPreferencesPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route path="patterns" element={<PatternCatalogPage />} />
            <Route path="technologies" element={<TechnologyCatalogPage />} />
            <Route path="desk" element={<ArchitectureDeskPage />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <CookieBanner onAccept={handleAcceptCookies} onDecline={handleDeclineCookies} />
    </>
  );
}

interface AppProps { msalInstance: IPublicClientApplication; }

const App: React.FC<AppProps> = ({ msalInstance }) => (
  <AppInsightsContext.Provider value={reactPlugin}>
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </AuthProvider>
    </MsalProvider>
  </AppInsightsContext.Provider>
);

export default App;